// src/pages/OrderListPage.js
// FIXED CODE - Added useMemo import
// Updated with status filters, status dropdown, edit button, and print button

import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { get, put } from 'aws-amplify/api'; // Added put
import {
    Table,
    TableBody,
    TableHead,
    TableRow,
    TableCell,
    Button,
    TextField,
    View,
    Flex,
    Text,
    SelectField, // Added SelectField
    Pagination, // Assuming Pagination is used or available
} from '@aws-amplify/ui-react';
import { generateJobSheetPDF } from '../utils/pdfUtils';

const PAGE_SIZE = 50;
const ORDER_STATUSES = ["Entered", "Printed", "Completed", "On-Hold", "Cancelled"];
const COMPLETED_STATUS_LOWER = 'completed'; // Keep for default behavior if needed

export default function OrderListPage() {
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0); // This will be 1-indexed for Amplify UI Pagination
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('_dateValue');
    const [sortOrder, setSortOrder] = useState('desc');
    const [activeStatusFilter, setActiveStatusFilter] = useState(null); // null for default (e.g. hide completed), or specific status

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                setLoading(true);
                setErrorMsg('');
                const restOp = get({
                    apiName: 'cbx_CRUD_API',
                    path: '/unifiedorders',
                });
                const response = await restOp.response;
                if (response.statusCode < 200 || response.statusCode > 299) {
                    throw new Error(`HTTP ${response.statusCode} error fetching orders`);
                }
                const orders = await response.body.json();
                if (!Array.isArray(orders)) {
                    throw new Error('Did not receive an array of orders');
                }

                const flattened = [];
                for (const ord of orders) {
                    const dateValue = ord.orderDate ? new Date(ord.orderDate).getTime() : 0;
                    if (!Array.isArray(ord.items) || ord.items.length === 0) {
                        flattened.push({
                            ...ord,
                            _dateValue: dateValue,
                            _lineIndex: 0,
                            price: '', quantity: '', Board_Grade: '', Box_Style: '',
                            notes: '', adminNotes: '',
                            _totalLength: '', _totalWidth: '', _totalTime: '', _totalSheets: '',
                            length: '', width: '', height: '',
                        });
                    } else {
                        for (const [idx, li] of ord.items.entries()) {
                            const props = li.properties || {};
                            flattened.push({
                                ...ord,
                                _dateValue: dateValue,
                                _lineIndex: idx,
                                price: li.price, quantity: li.quantity,
                                Board_Grade: props.Board_Grade, Box_Style: props.Box_Style,
                                notes: props.notes, adminNotes: props.adminNotes,
                                _totalLength: props._totalLength, _totalWidth: props._totalWidth,
                                _totalTime: props._totalTime, _totalSheets: props._totalSheets,
                                length: props.Length || props.length,
                                width: props.Width || props.width,
                                height: props.Height || props.height,
                            });
                        }
                    }
                }
                flattened.sort((a, b) => b._dateValue - a._dateValue);
                setRows(flattened);
            } catch (err) {
                console.error('[OrderListPage] fetch error:', err);
                setErrorMsg(`Failed to fetch orders: ${err.message}. Check API endpoint and network.`);
            } finally {
                setLoading(false);
            }
        };
        fetchOrders();
    }, []);

    function removePrefix(fullId) {
        if (!fullId) return '';
        const idx = fullId.indexOf('#');
        return idx >= 0 ? fullId.substring(idx + 1) : fullId;
    }

    function formatDate(isoStr) {
        if (!isoStr) return '';
        try {
            const d = new Date(isoStr);
            return isNaN(d.getTime()) ? isoStr : d.toLocaleDateString('en-NZ');
        } catch (e) {
            return isoStr;
        }
    }

    // --- Filtering Logic ---
    const filteredByStatusRows = useMemo(() => {
        if (!activeStatusFilter) { // Default: Show all non-completed
            return rows.filter(row => (row.OrderStatus || '').toLowerCase() !== COMPLETED_STATUS_LOWER);
        }
        if (activeStatusFilter === 'All') { // Explicit "All" shows everything including completed
             return rows;
        }
        return rows.filter(row => (row.OrderStatus || '').toLowerCase() === activeStatusFilter.toLowerCase());
    }, [rows, activeStatusFilter]);

    const searchedRows = useMemo(() => {
        if (!searchTerm.trim()) {
            return filteredByStatusRows;
        }
        const search = searchTerm.toLowerCase();
        return filteredByStatusRows.filter((row) => (
            removePrefix(row.orderId).toLowerCase().includes(search) ||
            (row.source || '').toLowerCase().includes(search) ||
            (row.orderDate && formatDate(row.orderDate).toLowerCase().includes(search)) ||
            (row.OrderStatus || '').toLowerCase().includes(search) ||
            (row.quantity?.toString() || '').toLowerCase().includes(search) ||
            (row.Board_Grade || '').toLowerCase().includes(search) ||
            (row.Box_Style || '').toLowerCase().includes(search) ||
            (row.notes || '').toLowerCase().includes(search) ||
            (row.adminNotes || '').toLowerCase().includes(search)
        ));
    }, [filteredByStatusRows, searchTerm]);

    const sortedRows = useMemo(() => {
        return [...searchedRows].sort((a, b) => {
            if (!sortField) return 0;
            let aValue, bValue;
            if (sortField === 'orderId') {
                aValue = removePrefix(a.orderId).toLowerCase();
                bValue = removePrefix(b.orderId).toLowerCase();
            } else if (sortField === 'orderDate' || sortField === '_dateValue') {
                aValue = a._dateValue || 0;
                bValue = b._dateValue || 0;
            } else if (sortField === 'quantity') {
                aValue = parseFloat(a.quantity || 0);
                bValue = parseFloat(b.quantity || 0);
            } else { // Covers OrderStatus and other string fields
                aValue = (a[sortField] || '').toString().toLowerCase();
                bValue = (b[sortField] || '').toString().toLowerCase();
            }
            if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
    }, [searchedRows, sortField, sortOrder]);
    
    // Pagination
    const totalPages = Math.ceil(sortedRows.length / PAGE_SIZE);
    const currentPageIndex = page; // Assuming page state is 0-indexed for logic, 1-indexed for display
    const startIndex = currentPageIndex * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    const pageRows = sortedRows.slice(startIndex, endIndex);

    const handlePageChange = (newPageIndex) => { // For Amplify UI Pagination (1-indexed)
        setPage(newPageIndex -1); // Store as 0-indexed internally
    };
    
    const handleSort = (field) => {
        const actualSortField = field === 'orderDate' ? '_dateValue' : field;
        if (sortField === actualSortField) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(actualSortField);
            setSortOrder('asc');
        }
        setPage(0);
    };

    function getSortIndicator(field) {
        const actualSortField = field === 'orderDate' ? '_dateValue' : field;
        if (sortField === actualSortField) {
            return sortOrder === 'asc' ? ' ▲' : ' ▼';
        }
        return '';
    }

    const handlePdfAction = async (row, outputType) => { // Make it async
        try {
            generateJobSheetPDF(row, outputType);

            if (outputType === 'print') {
                // Assuming generateJobSheetPDF initiated the print dialog successfully
                // Update status to "Printed"
                if (ORDER_STATUSES.includes("Printed")) {
                    await handleStatusUpdate(row.orderId, row._lineIndex, "Printed");
                } else {
                    console.warn("Status 'Printed' is not defined in ORDER_STATUSES. Cannot update status.");
                    // Optionally set an error message for the user if this is unexpected
                    // setErrorMsg("Configuration error: 'Printed' status not available.");
                }
            }
        } catch (pdfError) {
            console.error("Error during PDF action:", pdfError);
            setErrorMsg("Failed to process PDF action: " + pdfError.message);
        }
    };

    const handleStatusFilterChange = (status) => {
        setActiveStatusFilter(status);
        setPage(0); // Reset to first page
    };
    
    const handleStatusUpdate = async (orderId, originalLineIndex, newStatus) => {
        // Optimistically update UI for all line items of this order
        const originalRows = [...rows];
        setRows(prevRows => prevRows.map(r => 
            r.orderId === orderId ? { ...r, OrderStatus: newStatus } : r
        ));

        try {
            // Use the raw orderId from the row, but URL-encode it for the path parameter
            const apiPathId = encodeURIComponent(orderId);
            if (!apiPathId) {
                throw new Error("URL-encoded Order ID is empty. Original: " + orderId);
            }

            // Fetch the current order to ensure we PUT the complete object with the status change.
            const getOp = get({ apiName: 'cbx_CRUD_API', path: `/unifiedorders/${apiPathId}` });
            const currentOrderResponse = await getOp.response;
            if (!currentOrderResponse || currentOrderResponse.statusCode < 200 || currentOrderResponse.statusCode > 299) {
                 throw new Error(`Failed to fetch order ${orderId} (encoded: ${apiPathId}) before update. Status: ${currentOrderResponse?.statusCode}`);
            }
            const currentOrder = await currentOrderResponse.body.json();
            
            const updatedOrderPayload = { ...currentOrder, OrderStatus: newStatus };
            
            const putOp = put({
                apiName: 'cbx_CRUD_API',
                path: `/unifiedorders/${apiPathId}`, 
                options: {
                    body: updatedOrderPayload
                }
            });
            const updateResponse = await putOp.response;
            if (!updateResponse || updateResponse.statusCode < 200 || updateResponse.statusCode > 299) {
                throw new Error(`API error ${updateResponse?.statusCode} when updating status for order ${orderId} (encoded: ${apiPathId})`);
            }
            
            console.log(`Order ${orderId} status successfully updated to ${newStatus}`);
            // setErrorMsg(''); // Clear any previous error messages on success
            // Optionally, re-fetch all orders to ensure data consistency if other users might be changing data,
            // or if the PUT operation has side-effects not captured by the optimistic update.
            // For now, the optimistic update is considered sufficient for immediate UI feedback.

        } catch (err) {
            console.error('Failed to update order status:', err);
            setErrorMsg(`Failed to update status for ${orderId}: ${err.message}. Reverting UI change.`);
            setRows(originalRows); // Revert optimistic update on error
        }
    };

    const getTableCaption = () => {
        if (totalPages === 0) return 'No orders match current filters.';

        const baseText = `Page ${currentPageIndex + 1} of ${totalPages}. Showing ${pageRows.length} of ${sortedRows.length}`;
        if (activeStatusFilter === 'All') {
            return `${baseText} total orders.`;
        }
        if (activeStatusFilter) {
            return `${baseText} '${activeStatusFilter}' orders.`;
        }
        // Default filter (null)
        return `${baseText} non-completed orders.`;
    };

    // --- Render ---\n    if (loading) return <View padding="1rem">Loading orders...</View>;
    if (errorMsg) return <View padding="1rem" style={{ color: 'red', border: '1px solid red', backgroundColor: '#ffeeee' }}>Error: {errorMsg}</View>;
    if (rows.length === 0 && !loading) return <View padding="1rem">No orders found.</View>;

    return (
        <View padding="1rem">
            <h1>Order List</h1>
            <Flex direction="column" gap="1rem" marginBottom="1rem">
                <Flex direction="row" justifyContent="space-between" alignItems="flex-end" gap="1rem" wrap="wrap">
                    <TextField
                        label="Search Orders"
                        labelHidden
                        placeholder="Search orders..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPage(0); 
                        }}
                        size="small"
                        style={{ minWidth: '300px', flexGrow: 1 }}
                    />
                </Flex>
                <Flex direction="row" gap="0.5rem" alignItems="center" wrap="wrap">
                    <Text>Filter by Status:</Text>
                    <Button onClick={() => handleStatusFilterChange(null)} size="small" variation={activeStatusFilter === null ? "primary" : "default"}>Default (Hide Completed)</Button>
                    <Button onClick={() => handleStatusFilterChange('All')} size="small" variation={activeStatusFilter === 'All' ? "primary" : "default"}>Show All</Button>
                    {ORDER_STATUSES.map(status => (
                        <Button 
                            key={status} 
                            onClick={() => handleStatusFilterChange(status)}
                            size="small"
                            variation={activeStatusFilter === status ? "primary" : "default"}
                        >
                            {status}
                        </Button>
                    ))}
                </Flex>
            </Flex>

            <Table
                caption={getTableCaption()}
                style={{ width: '100%' }}
                variation="striped"
                highlightOnHover
                size="small"
            >
                <TableHead>
                    <TableRow>
                        <TableCell as="th" onClick={() => handleSort('orderId')}>Order/PO{getSortIndicator('orderId')}</TableCell>
                        <TableCell as="th" onClick={() => handleSort('source')}>Source{getSortIndicator('source')}</TableCell>
                        <TableCell as="th" onClick={() => handleSort('orderDate')}>Order Date{getSortIndicator('orderDate')}</TableCell>
                        <TableCell as="th" onClick={() => handleSort('OrderStatus')}>Status{getSortIndicator('OrderStatus')}</TableCell>
                        <TableCell as="th" onClick={() => handleSort('quantity')}>Qty{getSortIndicator('quantity')}</TableCell>
                        <TableCell as="th" onClick={() => handleSort('Board_Grade')}>Board Grade{getSortIndicator('Board_Grade')}</TableCell>
                        <TableCell as="th" onClick={() => handleSort('Box_Style')}>Box Style{getSortIndicator('Box_Style')}</TableCell>
                        <TableCell as="th" onClick={() => handleSort('notes')}>GT Narration{getSortIndicator('notes')}</TableCell>
                        <TableCell as="th" onClick={() => handleSort('adminNotes')}>Notes{getSortIndicator('adminNotes')}</TableCell>
                        <TableCell as="th">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {pageRows.map((row, index) => (
                        <TableRow key={`${row.orderId}-${row._lineIndex || index}`}> {/* Ensure unique key */}
                            <TableCell>{removePrefix(row.orderId)}{row._lineIndex > 0 ? `-${row._lineIndex + 1}` : ''}</TableCell>
                            <TableCell>{row.source}</TableCell>
                            <TableCell>{formatDate(row.orderDate)}</TableCell>
                            <TableCell>
                                <SelectField
                                    label="Status"
                                    labelHidden
                                    size="small"
                                    value={row.OrderStatus || ''}
                                    onChange={(e) => handleStatusUpdate(row.orderId, row._lineIndex, e.target.value)}
                                    style={{minWidth: "120px"}}
                                >
                                    <option value="" disabled>Select...</option>
                                    {ORDER_STATUSES.map(status => (
                                        <option key={status} value={status}>{status}</option>
                                    ))}
                                </SelectField>
                            </TableCell>
                            <TableCell>{row.quantity}</TableCell>
                            <TableCell>{row.Board_Grade}</TableCell>
                            <TableCell>{row.Box_Style}</TableCell>
                            <TableCell style={{ maxWidth: '150px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{row.notes}</TableCell>
                            <TableCell style={{ maxWidth: '150px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{row.adminNotes}</TableCell>
                            <TableCell>
                                <Flex direction="row" gap="0.25rem" wrap="nowrap">
                                    <Button onClick={() => handlePdfAction(row, 'download')} size="small" variation="link">PDF</Button>
                                    <Button onClick={() => handlePdfAction(row, 'print')} size="small" variation="link">Print</Button>
                                    <Link to={`/update/${encodeURIComponent(row.orderId)}`} style={{ marginRight: '0.25rem' }}>
                                        <Button size="small" variation="link">Edit</Button>
                                    </Link>
                                </Flex>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            {totalPages > 0 && (
                 <Flex justifyContent="center" marginTop="1rem">
                    <Pagination
                        currentPage={currentPageIndex + 1} // Amplify UI Pagination is 1-indexed
                        totalPages={totalPages}
                        onNext={() => handlePageChange(currentPageIndex + 2)}
                        onPrevious={() => handlePageChange(currentPageIndex)}
                        onChange={handlePageChange}
                    />
                </Flex>
            )}
        </View>
    );
}